import { Inject, inject, Injectable } from '@angular/core';
import { CleanupService } from '../../../../shared/utils/cleanup.service';
import { combineLatest, Observable, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterSelectItem } from '../../../../components/checkbox-facette/checkbox-liste/filterSelectItem';
import {
  AggregationKey,
  DefaultAggregations,
  LERNFORMEN,
  LernformKey
} from '../../../../model/aggregations';
import { groupedOrderedLernformen } from '../../../../model/lernformen';
import { FacettenService } from '../../facetten.service';
import { DigitaleLernformService } from './digitalelernform.service';
import { FernLernformService } from './fernlernform.service';
import { KombiLernformService } from './kombilernform.service';
import { getEnumValueByKey, LernformFacettenService } from './lernformfacetten.service';
import { LernformUrlService } from './lernformurl.service';
import { VorortLernformService } from './vorortlernform.service';

@Injectable({ providedIn: 'root' })
export class LernformService extends FacettenService {
  private filterAggregations!: DefaultAggregations;

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    urlService: LernformUrlService,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Inject(LernformFacettenService) private readonly facettenServices: LernformFacettenService[]
  ) {
    super(urlService, AggregationKey.Lernformen, groupedOrderedLernformen);
    this.filterAggregations$
      .pipe(takeUntil(inject(CleanupService).destroyed$))
      .subscribe((filterAggregations) => (this.filterAggregations = filterAggregations));
  }

  // /**
  //  * Überschreibt das getAggregations des FacettenServices,
  //  * um auf die genesteten Facetten der innerhalb der Lernformaggregation zugreifen zu können
  //  */
  override getAggregation(aggregations: DefaultAggregations) {
    const lernformAggregation = getEnumValueByKey(aggregations, this.aggregationName)!;
    return {
      VORORT: this.getAggregationAnzahl(lernformAggregation, VorortLernformService.identifier),
      DIGITAL: this.getAggregationAnzahl(lernformAggregation, DigitaleLernformService.identifier),
      KOMBI: this.getAggregationAnzahl(lernformAggregation, KombiLernformService.identifier),
      FERN: this.getAggregationAnzahl(lernformAggregation, FernLernformService.identifier)
    };
  }

  /**
   * Kombiniert die aktiven Lernformen
   */
  public getAllAktiveLernformen$(): Observable<FilterSelectItem[]> {
    return combineLatest([
      this.vorortLernformen$,
      this.fernLernformen$,
      this.digitaleLernformen$,
      this.kombiLernformen$
    ]).pipe(
      map(([vorort, fern, digital, kombi]) => {
        return [...vorort, ...fern, ...digital, ...kombi].filter((item) => item.active);
      })
    );
  }

  /**
   * Kombiniert die selektierten Lernformen
   */
  override getSelectItems$(): Observable<FilterSelectItem[]> {
    return combineLatest([
      this.vorortLernformen$,
      this.fernLernformen$,
      this.digitaleLernformen$,
      this.kombiLernformen$,
      this.facettenUrlService.selectedOptionIds$
    ]).pipe(
      map(([vorort, fern, digital, kombi, selectedIds]) => [
        this.createSelectItem(VorortLernformService.identifier, vorort, selectedIds),
        this.createSelectItem(FernLernformService.identifier, fern, selectedIds),
        this.createSelectItem(DigitaleLernformService.identifier, digital, selectedIds),
        this.createSelectItem(KombiLernformService.identifier, kombi, selectedIds)
      ])
    );
  }

  // Aktualisiert den ausgewählten Filter
  override updateSelectedIds(selectedIds: string[]): void {
    const mappedIds = selectedIds
      .map((key) =>
        groupedOrderedLernformen.find(({ id }) => id == key)!.ids.map((id) => String(id))
      )
      .flat();
    this.facettenUrlService.updateSelection(mappedIds);
  }

  override getHasItems$(): Observable<boolean> {
    return this.getSelectItems$().pipe(
      map((items) => {
        const anzahlOptionen = items?.reduce<number>((acc, { treffer }) => acc + treffer, 0);
        return anzahlOptionen > 0;
      })
    );
  }

  override isItemSelected(selectedIds: string[], key: string): boolean {
    const lernformAggregation = getEnumValueByKey(this.filterAggregations, this.aggregationName);
    const aggregation = getEnumValueByKey(lernformAggregation!, key);
    if (!!aggregation) {
      const ids = Object.keys(aggregation);
      const contains = ids.every((id) => selectedIds.includes(id));
      return contains;
    }
    return false;
  }

  override getNumberSelected$(): Observable<number> {
    return this.getSelectItems$().pipe(
      map((items) => items.filter(({ selected, active }) => selected && active).length)
    );
  }

  private createSelectItem(
    key: string,
    fiterItems: FilterSelectItem[],
    selectedIds: string[]
  ): FilterSelectItem {
    const label = this.orderedOptions.find((option) => option.id === key)?.bezeichnung!;
    return {
      key: key,
      ariaLabel: label || '',
      treffer: fiterItems.map((item) => item.treffer).reduce((a, b) => a + b, 0),
      label: label,
      selected: this.isItemSelected(selectedIds, key),
      active: true
    };
  }

  private getAggregationAnzahl(aggregations: LERNFORMEN, identifier: keyof LERNFORMEN): number {
    const aggregation = getEnumValueByKey(aggregations, identifier);
    return Object.entries(aggregation!)
      .map(([_, anzahl]) => {
        return anzahl;
      })
      .reduce((a, b) => a + b, 0);
  }

  /**
   * Liefert die selektierten Items der einzelnen Lernformen
   */
  private readonly getItemsByDetailName = (identifier: LernformKey) =>
    this.facettenServices
      .find(({ aggregationDetailName }) => aggregationDetailName == identifier)!
      .getSelectItems$();

  /**
   * Selektierte Lernform Items der jeweiligen Facette
   */
  public vorortLernformen$ = this.getItemsByDetailName(VorortLernformService.identifier);
  public digitaleLernformen$ = this.getItemsByDetailName(DigitaleLernformService.identifier);
  public fernLernformen$ = this.getItemsByDetailName(FernLernformService.identifier);
  public kombiLernformen$ = this.getItemsByDetailName(KombiLernformService.identifier);
}
